import React, { useState } from "react";
import { Layout, Loader2, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { analysisSuggestStructure, errorMessage } from "../../lib/api";

const VERDICT_STYLE = {
  on_target:  { pill: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "On target" },
  over:       { pill: "bg-amber-50 text-amber-700 border-amber-200",       label: "Too long" },
  under:      { pill: "bg-indigo-50 text-indigo-700 border-indigo-200",    label: "Too short" },
  missing:    { pill: "bg-red-50 text-red-700 border-red-200",             label: "Not found" },
  extra:      { pill: "bg-zinc-50 text-zinc-600 border-zinc-200",          label: "Extra" },
  no_target:  { pill: "bg-zinc-50 text-zinc-500 border-zinc-200",          label: "—" },
};

/** In-report Suggested Structure section. Shows Actual vs Suggested word counts
 *  and a single-sentence rebalance tip. Falls back to a "Compute now" button
 *  for legacy analyses that pre-dated the planner. */
export default function StructurePlannerReport({ analysisId, structure, onComputed }) {
  const [busy, setBusy] = useState(false);

  const computeNow = async () => {
    setBusy(true);
    try {
      const res = await analysisSuggestStructure(analysisId);
      onComputed?.(res);
      toast.success("Suggested structure ready.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  // Legacy analysis — no planner data. Show a "Show me" CTA.
  if (!structure || !structure.rows) {
    return (
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5" data-testid="structure-planner-cta">
        <div className="flex items-center gap-2 mb-2">
          <Layout size={16} className="text-indigo-600" />
          <p className="font-semibold text-sm text-zinc-900">Suggested Structure</p>
        </div>
        <p className="text-sm text-zinc-700 mb-3 leading-relaxed">
          See how your section word-counts compare to a suggested breakdown for this brief.
          MyAE will propose section-by-section targets and flag where to trim or expand.
        </p>
        <button
          onClick={computeNow}
          disabled={busy}
          data-testid="structure-planner-compute-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-3 py-1.5"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />}
          Show suggested structure
        </button>
      </div>
    );
  }

  const rows = structure.rows || [];
  const totalSuggested = rows.reduce((a, r) => a + (r.suggested_words || 0), 0);
  const totalActual = rows.reduce((a, r) => a + (r.actual_words || 0), 0);

  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/30 p-5" data-testid="structure-planner-report">
      <div className="flex items-center gap-2 mb-1">
        <Layout size={16} className="text-indigo-600" />
        <p className="font-semibold text-sm text-zinc-900">Suggested Structure</p>
        {structure.assessment_type && (
          <span className="text-[10px] font-semibold text-indigo-700 bg-white border border-indigo-200 rounded-full px-2 py-0.5">
            {structure.assessment_type}
          </span>
        )}
      </div>
      {structure.rationale && (
        <p className="text-xs text-zinc-600 mb-3 italic">{structure.rationale}</p>
      )}

      <div className="overflow-hidden rounded-lg border border-indigo-100 bg-white" data-testid="structure-planner-table">
        <table className="w-full text-sm">
          <thead className="bg-indigo-50/60 text-zinc-700">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide">Section</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wide">Suggested</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-wide">Actual</th>
              <th className="text-center px-3 py-2 font-semibold text-xs uppercase tracking-wide">Verdict</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-indigo-50">
            {rows.map((r, i) => {
              const v = VERDICT_STYLE[r.verdict] || VERDICT_STYLE.no_target;
              return (
                <tr key={i} data-testid={`structure-row-${i}`}>
                  <td className="px-3 py-2 text-zinc-900">
                    <div className="font-medium">{r.title}</div>
                    {r.purpose && <div className="text-[11px] text-zinc-500">{r.purpose}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-800">
                    {r.suggested_words > 0 ? r.suggested_words : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-800">
                    {r.actual_words > 0 ? r.actual_words : "—"}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full border px-2 py-0.5 inline-block ${v.pill}`}>
                      {v.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-indigo-50/40 text-zinc-700">
            <tr>
              <td className="px-3 py-2 font-semibold text-xs">Total</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">{totalSuggested || "—"}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">{totalActual || "—"}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {rows.map((r, i) => r.verdict === "over" || r.verdict === "under" || r.verdict === "missing" ? (
        <p key={i} className="text-xs text-zinc-600 mt-2" data-testid={`structure-recommendation-${i}`}>
          <span className="font-semibold text-zinc-800">{r.title}:</span> {r.recommendation}
        </p>
      ) : null)}

      {structure.rebalance_hint && (
        <div className="mt-4 rounded-lg border-l-4 border-indigo-500 bg-white px-4 py-3" data-testid="structure-rebalance-hint">
          <div className="flex items-start gap-2">
            <Lightbulb size={14} className="text-indigo-600 mt-0.5 shrink-0" />
            <p className="text-sm text-zinc-800"><b>Tip:</b> {structure.rebalance_hint}</p>
          </div>
        </div>
      )}
      {structure.actual_sections_detected === 0 && (
        <p className="text-xs text-amber-700 mt-2 italic">
          We couldn&apos;t detect distinct section headings in your submission — the &quot;Actual&quot; column will be blank until you use clear headings (e.g. Introduction, Conclusion).
        </p>
      )}
    </div>
  );
}
